import React, { useLayoutEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SbTag } from "uiCore";
import RedeploymentReductionDetailV2 from "./RedeploymentReductionDetailV2";
import PaicRoleCandidatesV2 from "./PaicRoleCandidatesV2";
import PlanSaveButton from "./components/PlanSaveButton";
import usePaicRedesignPlanActions from "./usePaicRedesignPlanActions";
import usePaicRedesignPlanStore from "Store/usePaicRedesignPlanStore";
import useRedeploymentPlanStore from "Store/useRedeploymentPlanStore";
import { PAIC_VERSIONS_PATH, ROLES_ADDED_API_STATUS } from "./PaicOrgRedesignV2.utils";

// PAIC Reduction Detail shows who is shortlisted through the row's
// Shortlist / Undo Shortlist button alone — no Available / Reason columns.
// Module-level so the page's column memo stays stable.
const PAIC_HIDDEN_COLUMN_KEYS = ["available", "shortlistReason"];

// PAIC Org Redesign's entry onto the shared Reduction Detail page
// (/talent-mobility/paic-org-redesign/reduction/:roleId?versionId=&roleKey=).
// The page picks employees into useRedeploymentPlanStore; this wrapper turns
// that into a scratchpad for ONE drafted PAIC role:
//
//   - on entry, the role's PAIC draft (target + employees) is loaded into the
//     redeployment store and the role is linked, so usePaicRedesignPlanStore's
//     subscription copies every later pick back into the PAIC draft;
//   - coming back from a Candidate Profile opened here, the role is still
//     linked and the redeployment store still holds the newer picks, so it
//     is left as is;
//   - the header's Save buttons save the PAIC plan, not a redeployment plan.
//
// An INCREASED role (opened from Roles Added) is growing, not shrinking, so it
// gets PaicRoleCandidatesV2 instead — the people who could fill it — which
// writes its picks straight into the PAIC draft (no redeployment store link).
//
// A role that isn't in the PAIC draft (direct link, or a reload — drafts are
// session-only) has nowhere to save its picks, so it goes back to the version
// page, which adds the version to the draft when a role is opened from it.
const PaicReductionDetailV2 = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const [searchParams] = useSearchParams();
  const versionId = searchParams.get("versionId");
  const roleKey = searchParams.get("roleKey") || roleId;
  const versionPath = versionId ? `${PAIC_VERSIONS_PATH}/${versionId}` : PAIC_VERSIONS_PATH;

  // Checked once, on entry: a Save & Close here clears the draft right
  // before navigating away, which must not also trigger this redirect.
  const [draftRoleOnEntry] = useState(() => usePaicRedesignPlanStore.getState().versions[versionId]?.roles[roleKey]);
  const isRoleDrafted = Boolean(draftRoleOnEntry);
  const isIncreasedRole = draftRoleOnEntry?.change_type === ROLES_ADDED_API_STATUS;
  const { hasDraft, isSubmitting, planName, planLabel, isUpdate, savePlan } = usePaicRedesignPlanActions({
    onClosed: () => navigate(PAIC_VERSIONS_PATH),
  });
  // Marks a Candidate Profile opened from this page as PAIC's, so its header
  // saves the PAIC plan rather than a redeployment plan.
  const profileLinkParams = useMemo(
    () => ({ paic_version_id: versionId || "", paic_role_key: roleKey }),
    [versionId, roleKey]
  );

  // Layout effect: runs before Reduction Detail's own passive effects (its
  // ?target= write), so the page starts from the seeded draft.
  useLayoutEffect(() => {
    if (isIncreasedRole) return;
    const paicStore = usePaicRedesignPlanStore.getState();
    const draftRole = paicStore.versions[versionId]?.roles[roleKey];
    if (!draftRole) return;
    const { linkedRole } = paicStore;
    if (linkedRole?.versionId === versionId && linkedRole?.roleKey === roleKey) return;

    // Unlink first: loadPlan resets the store, which must not be mirrored
    // over whichever role was linked before.
    paicStore.unlinkRole();
    useRedeploymentPlanStore.getState().loadPlan({
      planId: null,
      planName: "",
      redeploymentPlans: [
        {
          jd_sb_unique_id: roleId,
          available_employee_targets: draftRole.available_employee_targets,
          target_position: draftRole.target_position,
        },
      ],
    });
    paicStore.linkRole({ versionId, roleKey, jdId: roleId });
  }, [versionId, roleKey, roleId, isIncreasedRole]);

  if (!isRoleDrafted) return <Navigate to={versionPath} replace />;

  const saveButtonProps = { planTypeLabel: "PAIC Redesign Plan", hasDraft, isUpdate, planName, isSubmitting, onSave: savePlan };
  const planTag = (
    <SbTag color={isUpdate ? "blue" : "gold"} style={{ fontSize: 14 }}>
      {planLabel}
    </SbTag>
  );
  const planActions = (
    <>
      <PlanSaveButton {...saveButtonProps} />
      <PlanSaveButton {...saveButtonProps} buttonType="secondary" closeAfter />
    </>
  );

  if (isIncreasedRole) {
    // ?current= is the role's current headcount, passed by the version page.
    const currentParam = searchParams.get("current");
    return (
      <PaicRoleCandidatesV2
        versionId={versionId}
        roleKey={roleKey}
        roleId={roleId}
        title={searchParams.get("title") || roleId}
        currentPositions={currentParam ? Number(currentParam) : null}
        targetPositions={draftRoleOnEntry.target_position}
        onBack={() => navigate(versionPath)}
        planTag={planTag}
        planActions={planActions}
      />
    );
  }

  return (
    <RedeploymentReductionDetailV2
      backPath={versionPath}
      backLabel="Back to PAIC Org Redesign"
      exportLabel="Export CSV"
      exportFilePrefix="paic-org-redesign-reduction"
      hiddenColumnKeys={PAIC_HIDDEN_COLUMN_KEYS}
      profileLinkParams={profileLinkParams}
      planHeader={{ tag: planTag, actions: planActions }}
    />
  );
};

export default PaicReductionDetailV2;
